import React from "react"

import {
  HeroSection,
  AboutSection,
  ServicesSection,
  ContactSection,
  CTASection,
  PackagesSection,
  EventsSection,
  CredentialsSection,
  CTABannerSection,
  KeywordsSection,
  HeaderSection,
  FooterSection,
  FAQSection,
  ReviewsSection,
  GallerySection,
  QuoteSection,
  SubHeroSection,
  GenericSection,
  ReviewHeroSection,
  BlogPostsSection
} from "./components"
import { ScrollReveal } from "./components/shared/ScrollReveal"
import { isReviewsPageType } from "./utils/pageUtils"

const SectionRenderer = ({
  section,
  practice,
  theme,
  highlightedField,
  pageSlug = null,
  defaultPageType = null,
  viewMode = "",
  deviceType = "desktop",
  isCustom = false, // eslint-disable-line no-unused-vars
  viewingInBuilder = false
}) => {
  const commonProps = { section, practice, theme, highlightedField, viewMode, deviceType }

  const layoutsWithoutSectionMotion = new Set(["header", "hero", "sub_hero", "review_hero"])
  const sectionMotionDisabled = viewingInBuilder || layoutsWithoutSectionMotion.has(section.layout)

  // Create anchor ID for the section - include type, layout variant (if available), and id for better identification
  const layoutVariant = section.settings?.layout
  const sectionAnchorId =
    section.layout === "events"
      ? "events-section-full"
      : layoutVariant
      ? `section-${section.layout}-${layoutVariant}-${section.id}`
      : `section-${section.layout}-${section.id}`

  // Wrap the section with an anchor element for scrolling
  const renderSectionWithAnchor = (sectionComponent) => (
    <div id={sectionAnchorId} className="scroll-mt-20">
      <ScrollReveal variant="fadeIn" disabled={sectionMotionDisabled} threshold={0.06}>
        {sectionComponent}
      </ScrollReveal>
    </div>
  )

  const isReviewsPage = isReviewsPageType(defaultPageType, pageSlug, practice)

  switch (section.layout) {
    case "hero": {
      // Use HeroSection for home page, or when a custom page opts in via settings (see HeroEditor).
      // Reviews page uses ReviewHeroSection; other pages default to SubHeroSection.
      const useHomepageHero = section.settings?.useHomepageHero === true
      const shouldUseHeroSection = defaultPageType === "home" || useHomepageHero

      if (isReviewsPage) {
        return renderSectionWithAnchor(
          <ReviewHeroSection {...commonProps} pageSlug={pageSlug} defaultPageType={defaultPageType} />
        )
      }

      return renderSectionWithAnchor(
        shouldUseHeroSection ? <HeroSection {...commonProps} /> : <SubHeroSection {...commonProps} />
      )
    }

    case "sub_hero": {
      if (isReviewsPage) {
        return renderSectionWithAnchor(
          <ReviewHeroSection {...commonProps} pageSlug={pageSlug} defaultPageType={defaultPageType} />
        )
      }

      return renderSectionWithAnchor(<SubHeroSection {...commonProps} />)
    }

    case "review_hero":
      return renderSectionWithAnchor(
        <ReviewHeroSection {...commonProps} pageSlug={pageSlug} defaultPageType={defaultPageType} />
      )

    case "about":
      return renderSectionWithAnchor(<AboutSection {...commonProps} />)

    case "services":
    case "featured_services":
      // Both services and featured_services now use the unified ServicesSection
      // The component automatically handles different layouts
      return renderSectionWithAnchor(
        <ServicesSection {...commonProps} pageSlug={pageSlug} defaultPageType={defaultPageType} />
      )

    case "contact":
      return renderSectionWithAnchor(<ContactSection {...commonProps} viewingInBuilder={viewingInBuilder} />)

    case "cta_content":
      return renderSectionWithAnchor(<CTASection {...commonProps} />)

    case "packages":
      return renderSectionWithAnchor(
        <PackagesSection {...commonProps} pageSlug={pageSlug} defaultPageType={defaultPageType} />
      )

    case "events":
      return renderSectionWithAnchor(
        <EventsSection {...commonProps} pageSlug={pageSlug} defaultPageType={defaultPageType} />
      )

    case "credentials":
      return renderSectionWithAnchor(<CredentialsSection {...commonProps} />)

    case "cta_banner":
      return renderSectionWithAnchor(<CTABannerSection {...commonProps} />)

    case "keywords":
      return renderSectionWithAnchor(<KeywordsSection {...commonProps} />)

    case "header": {
      // Always show header by default, unless explicitly set to false
      const showHeaderSetting = section.settings?.showHeader
      const shouldShowHeader = showHeaderSetting !== undefined ? !!showHeaderSetting : true

      if (!shouldShowHeader) return null

      return renderSectionWithAnchor(<HeaderSection {...commonProps} />)
    }

    case "footer":
      return renderSectionWithAnchor(<FooterSection {...commonProps} />)

    case "faq":
      return renderSectionWithAnchor(<FAQSection {...commonProps} />)

    case "quote":
      return renderSectionWithAnchor(<QuoteSection {...commonProps} />)

    case "reviews":
      return renderSectionWithAnchor(
        <ReviewsSection {...commonProps} pageSlug={pageSlug} defaultPageType={defaultPageType} />
      )

    case "gallery":
      return renderSectionWithAnchor(<GallerySection {...commonProps} />)

    case "blog_posts":
      return renderSectionWithAnchor(<BlogPostsSection {...commonProps} viewingInBuilder={viewingInBuilder} />)

    // Sections that still use the generic renderer
    default:
      return renderSectionWithAnchor(<GenericSection {...commonProps} />)
  }
}

export default SectionRenderer
